import {
  Activity,
  ArrowLeftRight,
  BadgeDollarSign,
  BookText,
  Box,
  Building2,
  Calculator,
  ChartArea,
  ChartBarBig,
  ChartBarDecreasing,
  ChartBarStacked,
  ChartCandlestick,
  ChartColumn,
  ChartGantt,
  ChartLine,
  ChartNoAxesCombined,
  ChartPie,
  ChartScatter,
  ChartSpline,
  CircleDashed,
  CircleDotDashed,
  CircleGauge,
  CircleQuestionMark,
  Columns2,
  Funnel,
  Gauge,
  GitBranch,
  GitFork,
  Grid2x2,
  Grid2x2Check,
  Grid3x3,
  Hash,
  Image,
  LayoutGrid,
  LayoutList,
  Lightbulb,
  ListChecks,
  ListOrdered,
  MapPin,
  MessageSquareHeart,
  Milestone,
  Network,
  Orbit,
  Presentation,
  Quote,
  Radar,
  RefreshCw,
  Rows3,
  ScanEye,
  Sparkles,
  Split,
  SquareSplitHorizontal,
  Sun,
  Table,
  Table2,
  TableOfContents,
  Tags,
  Triangle,
  Users,
  Waypoints,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import { getModuleTypeLabel } from "@/lib/module-type-label";
import { MODULE_CATEGORIES, type AllModuleTypeNames } from "@/types/modules";

export interface ModuleTypeMeta {
  label: string;
  icon: LucideIcon;
}

export const MODULE_TYPE_META: Record<AllModuleTypeNames, ModuleTypeMeta> = {
  bar: { label: "Bar chart", icon: ChartColumn },
  line: { label: "Line chart", icon: ChartLine },
  pie: { label: "Pie chart", icon: ChartPie },
  scatter: { label: "Scatter plot", icon: ChartScatter },
  area: { label: "Area chart", icon: ChartArea },
  image: { label: getModuleTypeLabel("image"), icon: Image },
  progress: { label: getModuleTypeLabel("progress"), icon: Gauge },
  quote: { label: getModuleTypeLabel("quote"), icon: Quote },
  "feature-comparison": { label: "Feature comparison", icon: Table },
  table: { label: "Data table", icon: Table2 },
  "ring-kpis": { label: "Ring KPIs", icon: CircleDotDashed },
  "metric-tabs-chart": { label: "Metric tabs chart", icon: ChartSpline },
  breakdown: { label: "Breakdown", icon: ChartBarBig },
  "dual-metric": { label: "Dual metric", icon: SquareSplitHorizontal },
  "stage-bars": { label: "Stage bars", icon: ChartBarDecreasing },
  "insight-panel": { label: "Insight panel", icon: Sparkles },
  intro: { label: getModuleTypeLabel("intro"), icon: Presentation },
  toc: { label: "Table of contents", icon: TableOfContents },
  feedback: { label: getModuleTypeLabel("feedback"), icon: MessageSquareHeart },
  pyramid: { label: getModuleTypeLabel("pyramid"), icon: Triangle },
  "dotted-map": { label: "Dotted map", icon: MapPin },
  "radial-bar": { label: "Radial bar", icon: CircleGauge },
  candlestick: { label: getModuleTypeLabel("candlestick"), icon: ChartCandlestick },
  funnel: { label: getModuleTypeLabel("funnel"), icon: Funnel },
  heatmap: { label: getModuleTypeLabel("heatmap"), icon: Grid3x3 },
  "profit-loss": { label: "Profit and loss", icon: ChartNoAxesCombined },
  radar: { label: "Radar chart", icon: Radar },
  sankey: { label: "Sankey diagram", icon: Waypoints },
  sunburst: { label: getModuleTypeLabel("sunburst"), icon: Sun },
  "progress-stages": { label: "Progress stages", icon: ListChecks },
  "fork-join": { label: "Fork and join", icon: GitFork },
  "gantt-phases": { label: "Gantt phases", icon: ChartGantt },
  "parallel-streams": { label: "Parallel streams", icon: Rows3 },
  "segmented-progress": { label: "Segmented progress", icon: ChartBarStacked },
  "state-machine": { label: "State machine", icon: Workflow },
  "timeline-stepper": { label: "Timeline stepper", icon: Milestone },
  "quadrant-matrix": { label: "Quadrant matrix", icon: Grid2x2 },
  "correlation-heatmap": { label: "Correlation heatmap", icon: LayoutGrid },
  "skill-level-matrix": { label: "Skill level matrix", icon: Grid2x2Check },
  "node-cycle": { label: "Node cycle", icon: RefreshCw },
  "segmented-cycle": { label: "Segmented cycle", icon: CircleDashed },
  "ecosystem-map": { label: "Ecosystem map", icon: Orbit },
  "hierarchy-tree": { label: "Hierarchy tree", icon: Network },
  "parallel-category-trees": { label: "Category trees", icon: GitBranch },
  "activity-feed": { label: "Activity feed", icon: Activity },
  "metric-list": { label: "Metric list", icon: ListOrdered },
  "delta-comparison": { label: "Delta comparison", icon: ArrowLeftRight },
  "split-comparison": { label: "Split comparison", icon: Columns2 },
  "social-proof-stats": { label: "Social proof", icon: Users },
  "list-cards": { label: "List cards", icon: LayoutList },
  "big-number-chart": { label: "Big number", icon: Hash },
  "pricing-tiers": { label: "Pricing tiers", icon: BadgeDollarSign },
  "roi-calculator": { label: "ROI calculator", icon: Calculator },
  "logo-wall": { label: "Logo wall", icon: Building2 },
  "before-after-annotated": { label: "Before and after", icon: ScanEye },
  "before-after-slider": { label: "Before and after slider", icon: Split },
  "keywords-grouped": { label: "Grouped keywords", icon: Tags },
  "keywords-explained": { label: "Explained keywords", icon: BookText },
  "key-takeaways": { label: "Key takeaways", icon: Lightbulb },
  faq: { label: getModuleTypeLabel("faq"), icon: CircleQuestionMark },
};

export const MODULE_TYPE_NAMES = Object.keys(MODULE_TYPE_META) as AllModuleTypeNames[];

export type ModuleCategoryId = "charts" | "overview" | "texts" | "media";

export const MODULE_CATEGORY_ORDER: readonly { id: ModuleCategoryId; label: string }[] = [
  { id: "charts", label: "Charts" },
  { id: "overview", label: "Overview" },
  { id: "texts", label: "Texts" },
  { id: "media", label: "Media" },
];

const FALLBACK_META: ModuleTypeMeta = { label: "Module", icon: Box };

export function getModuleTypeMeta(type: string | null | undefined): ModuleTypeMeta {
  if (type && type in MODULE_TYPE_META) {
    return MODULE_TYPE_META[type as AllModuleTypeNames];
  }
  return { ...FALLBACK_META, label: type ? getModuleTypeLabel(type) : FALLBACK_META.label };
}

export function getModuleCategory(type: AllModuleTypeNames): ModuleCategoryId {
  const category = MODULE_CATEGORIES[type];
  return category === "charts" || category === "overview" || category === "texts" || category === "media"
    ? category
    : "overview";
}
